/**
 * Reimbursement Link Context
 *
 * Provides a way to open the reimbursement link modal from anywhere in the app
 * (e.g., notification item click). The provider wraps the app layout and renders
 * the modal when state is set.
 */

import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import { ReimbursementLinkModal } from './reimbursement-link-modal';

interface ReimbursementLinkState {
  transactionId: string;
  notificationId: string;
  notificationMetadata?: Record<string, unknown> | null;
  actionLabel?: string | null;
}

interface ReimbursementLinkContextValue {
  openLinkModal: (transactionId: string, notificationId: string, metadata?: Record<string, unknown> | null, actionLabel?: string | null) => void;
}

const ReimbursementLinkContext = createContext<ReimbursementLinkContextValue | null>(null);

export function ReimbursementLinkProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<ReimbursementLinkState | null>(null);

  const openLinkModal = useCallback((transactionId: string, notificationId: string, metadata?: Record<string, unknown> | null, actionLabel?: string | null) => {
    setState({ transactionId, notificationId, notificationMetadata: metadata, actionLabel });
  }, []);

  const handleClose = useCallback(() => {
    setState(null);
  }, []);

  return (
    <ReimbursementLinkContext.Provider value={useMemo(() => ({ openLinkModal }), [openLinkModal])}>
      {children}
      {state && (
        <ReimbursementLinkModal
          transactionId={state.transactionId}
          notificationId={state.notificationId}
          notificationMetadata={state.notificationMetadata}
          actionLabel={state.actionLabel}
          onClose={handleClose}
        />
      )}
    </ReimbursementLinkContext.Provider>
  );
}

export function useReimbursementLink(): ReimbursementLinkContextValue {
  const context = useContext(ReimbursementLinkContext);
  if (!context) {
    throw new Error('useReimbursementLink must be used within a ReimbursementLinkProvider');
  }
  return context;
}
